import React, { useRef, useEffect } from 'react';
import { classNames } from '../../../utils/compatibility';

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Whether to show a close button in the header */
  showCloseButton?: boolean;
  /** Custom class name for the modal container */
  className?: string;
  /** Custom class name for the backdrop */
  backdropClassName?: string;
  /** Size of the modal */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Child elements */
  children: React.ReactNode;
  /** Whether to close when clicking the backdrop */
  closeOnBackdropClick?: boolean;
  /** Whether the modal is static (doesn't close on backdrop click or escape key) */
  static?: boolean;
  /** Whether to disable scrolling on the body when open */
  disableBodyScroll?: boolean;
  /** Initial focus element */
  initialFocus?: React.RefObject<HTMLElement>;
}

/**
 * Modal Component
 * 
 * A dialog window that appears on top of the main content
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  showCloseButton = true,
  className = '',
  backdropClassName = '',
  size = 'md',
  children,
  closeOnBackdropClick = true,
  static: isStatic = false,
  disableBodyScroll = true,
  initialFocus,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Size classes
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full',
  };
  
  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && closeOnBackdropClick && !isStatic) {
      onClose();
    }
  };
  
  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isStatic) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isStatic, onClose]);
  
  // Focus management
  useEffect(() => {
    if (isOpen) {
      const focusElement = initialFocus?.current || modalRef.current;
      if (focusElement) {
        setTimeout(() => {
          focusElement.focus();
        }, 0);
      }
    }
  }, [isOpen, initialFocus]);
  
  // Disable body scroll when modal is open
  useEffect(() => {
    if (disableBodyScroll) {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, disableBodyScroll]);
  
  if (!isOpen) {
    return null;
  }
  
  return (
    <div
      className={classNames(
        'fixed inset-0 z-50 overflow-hidden',
        backdropClassName
      )}
      aria-labelledby={title ? 'modal-title' : undefined}
      role="dialog"
      aria-modal="true"
      onTouchMove={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        aria-hidden="true"
        onClick={handleBackdropClick}
        onTouchMove={(e) => e.preventDefault()}
      />
      
      {/* Modal position wrapper */}
      <div
        className="fixed inset-0 flex items-end justify-center p-4 text-center sm:items-center sm:p-0 pointer-events-none"
        style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom, 20px) + 16px)' }}
      >
        {/* Modal panel */}
        <div
          ref={modalRef}
          className={classNames(
            'relative transform rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all w-full max-h-[90vh] flex flex-col pointer-events-auto',
            'sm:my-8',
            sizeClasses[size],
            className
          )}
          tabIndex={-1}
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between">
              {title && (
                <h3
                  id="modal-title"
                  className="text-lg font-medium text-gray-900 dark:text-gray-100"
                >
                  {title}
                </h3>
              )}
              
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-400 hover:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-600"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
          
          {/* Content */}
          <div
            className="relative flex-1 min-h-0 overflow-y-auto"
            style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;